'use strict';
const _ = require('underscore');
const Moment = require('moment');
const Backbone = require('backbone');

const MAX_ACTION_LEVEL = 4;

const Action = Backbone.Model.extend({
    defaults: {
        name: '',
        state: null,
        stateType: null,
        type: null,
        point: {x: 0, y: 0},  // x, y point
        parent: null,  // parent process ID
        works: null,
        relatedWorks: [],
        assignee: null,
        assignees: [],
        startAt: null,
        endAt: null,
        deleted: false,
        _project: null,
        _children: null,  // child actions
        _type: null,  // action type
        _repository: null  // action repository
    },
    validation: {
        name: {
            required: true
        }
    },

    initialize: function() {
        this.set('_flows', new Backbone.Collection());
        this.set('_children', new Backbone.Collection());
    },

    path: function() {
        const projectId = this.get('_project').id;
        if(!this.get('parent')) {
            return `/projects/${projectId}/processes/`;
        } else {
            return `/projects/${projectId}/processes/${this.id}`;
        }
    },

    toJSON: function() {
        let j = Action.__super__.toJSON.call(this);
        j.parent = this.getParent() ? this.getParent().id : null;
        j.assignee = null;
        delete j.assignees;
        j.assignees = this.getAssignees().map(a => a.id);
        j.state = this.getCurrentState().id;
        j.startAt = j.startAt ? j.startAt.utc().format() : null;
        j.endAt = j.endAt ? j.endAt.utc().format() : null;

        _.each(_.keys(j), k => {
            if(k.startsWith('_')) {
                delete j[k];
            }
        });
        return j;
    },

    getParent: function() {
        return this.get('parent');
    },

    getAllParentsWithoutProject: function() {
        const parents = [];
        let parent = this.getParent();
        while(true) {
            if(parent && parent.getParent()) {
                parents.push(parent);
                parent = parent.getParent();
            } else {
                break;
            }
        }

        parents.reverse();
        return parents;
    },

    isMaxLevel: function() {
        // 親の数なので 1 を引いて計算
        return this.getAllParentsWithoutProject().length >= MAX_ACTION_LEVEL - 1;
    },

    isAncestorOf: function(action) {
        if(this == action) {
            return true;
        } else {
            const parent = action.get('parent');
            if(parent) {
                return this.isAncestorOf(parent);
            } else {
                return false;
            }
        }
    },

    isDeleted: function() {
        return this.get('deleted');
    },

    getAssignees: function() {
        return this.get('assignees');
    },

    setAssignees: function(assignees) {
        return this.set({assignees: assignees});
    },

    getType: function() {
        return this.get('_type');
    },

    getUpdatedAt: function() {
        return Moment(this.get('updatedAt'));
    },

    getUpdatedBy: function() {
        return this.get('_updatedBy');
    },
    setUpdatedBy: function(updatedBy) {
        return this.set({_updatedBy: updatedBy});
    },

    getName: function() {
        return this.get('name');
    },

    getDescription: function() {
        return this.get('description');
    },

    getStartAt: function() {
        return this.get('startAt');
    },

    getEndAt: function() {
        return this.get('endAt');
    },

    getSortPosition: function() {
        return this.get('sortPosition');
    },

    getProject: function() {
        return this.get('_project');
    },

    addChild: function(process) {
        process.set('parent', this);
        this.get('_children').add(process, {merge: true});
    },

    removeChild: function(action) {
        this.get('_children').remove(action);
    },

    getChildren: function(options) {
        return new Promise((resolve, reject) => {
            /*
             サブアクションを保持している場合には hasChildren プロパティが true に設定されている。
             hasChildren プロパティが true または、 option で強制取得が設定されている場合にのみHTTPリクエストを発火し、
             それ以外の場合にはHTTPリクエストの発火を省略することで、高速化を図る。
             */
            const children = this.get('_children');
            const fetchNeeded = this.get('hasChildren');
            const isForceMode = (options && options.force);

            if (fetchNeeded || isForceMode) {
                this.get('_repository').getChildrenOf(this.id)
                    .then(ps => {
                        children.add(ps, {merge: true});
                        children.fetched = true;
                        resolve(children);
                    }, reject);
            } else {
                resolve(children);
            }
        });
    },

    getChildrenRecursive: function(options) {
        return new Promise((resolve, reject) => {
            this.getChildren(options)
                .then(children => {
                    Promise.all(
                        children.map(child => {
                            return child.getChildrenRecursive(options);
                        })
                    )
                        .then(() => {
                            resolve(children);
                        })
                        .catch(reject);
                })
                .catch(reject);
        });
    },

    getProceedableStates: function() {
        return this.getType().getProceedableStates(this.getCurrentState());
    },

    proceedState: function(state) {
        this.set('state', state.id);
        this.set('stateType', state.type);
        if(state.getType().name == 'done') {
            _.each(this.getOperations(), op => {
                op.onStateDone();
            });
        }
    },

    getCurrentState: function() {
        return this.getType().getState(this.get('state'));
    },

    getStates: function() {
        return this.getType().getStates();
    },

    getState: function(stateId) {
        return this.getType().getState(stateId);
    },

    getStateByType: function(typeName) {
        return this.getType().getStateByType();
    },

    doReview: function() {
        return this.get('doReview');
    },

    canAddState: function() {
        return false;
    },

    getPoint: function() {
        return this.get('point');
    },

    getShape: function() {
        return 'rect';
    },

    getSize: function() {
        return {
            width: 384,
            height: 256,
            rx: 16,
            ry: 16
        };
    },

    hasComment: function () {
        return this.get('hasComment');
    },

    hasTask: function () {
        return this.get('hasTask');
    },

    active: function() {
        return !(this.inactive());
    },

    inactive: function() {
        const state = this.getCurrentState();
        return state.isFinal() || state.isTerminated();
    },

    // 開始日までの残り日数
    getStartLastDays: function() {
        const today = Moment();
        const startAt = this.getStartAt();

        let startLastDays;
        if(startAt) {
            startLastDays = Moment(startAt).diff(today, 'days');
        }
        return startLastDays;
    },

    // アクションが開始日までに開始していない（状態が未着手である & 開始日を過ぎている）
    startIsDelayed: function() {
        const isInitial = this.getCurrentState().isInitial();
        return isInitial && this.getStartLastDays() < 0;
    },

    // 終了日までの残り日数
    getEndLastDays: function() {
        const today = Moment();
        const endAt = this.getEndAt();

        let endLastDays;
        if(endAt) {
            endLastDays = Moment(endAt).diff(today, 'days');
        }
        return endLastDays;
    },

    // アクションが終了日までに完了/中止していない（状態が完了/キャンセルでない & 終了日を過ぎている）
    endIsDelayed: function() {
        const isActive = this.active();
        return isActive && this.getEndLastDays() < 0;
    },

    isDelayed: function() {
        return this.startIsDelayed() || this.endIsDelayed();
    }
});

module.exports = Action;



// WEBPACK FOOTER //
// ./process/domain/entity/action/index.js